import { DateTime } from 'luxon';
import { reportException } from 'src/exceptionReporting';
import * as moment from 'moment-timezone';
import { pathOr, toString } from 'ramda';
import { isNotEmpty, returnIsNotEmptyOrTrue } from './hasValue';
import { addDaysFromGivenDate, getDaysDifferenceBetweenTwoDate, getMonthsDifferenceBetweenTwoDate } from './formatDate';

export const getDate = () => {
  const today = new Date();
  const dd = today.getDate();
  const mm = today.getMonth() + 1;
  const yyyy = today.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};
export const parseAPIDate = (date: string | number) => {
  let date1;
  if (typeof date === 'string') {
    date1 = DateTime.fromISO(date, { zone: 'utc' });
  } else if (typeof date === 'number') {
    date1 = DateTime.fromMillis(date, { zone: 'utc' });
  }
  if (date1?.isValid) {
    return date1;
  }
  const err = new Error(`invalid date format: ${date}`);
  reportException(`<----date-23-----> ${err}`);
  throw err;
};
export const isBefore = (d1: string, d2: string) => {
  const date1 = parseAPIDate(d1);
  const date2 = parseAPIDate(d2);
  return date1 < date2;
};
export const isAfter = (d1: string, d2: string) => {
  const date1 = parseAPIDate(d1);
  const date2 = parseAPIDate(d2);
  return date1 > date2;
};
export const getDotDashDate = () => {
  const today = new Date();
  const mutc = moment.utc(today);
  return {
    dot: `${mutc.format('DD.MM.YYYY')}`,
    dash: `${mutc.format('YYYY-MM-DD')}`,
  };
};

export const addYearDotDate = (years: any) => {
  var d = new Date();
  d.setFullYear(d.getFullYear() + years);
  const a = d.toLocaleString();
  const mutc = moment.utc(a);
  return `${mutc.format('DD.MM.YYYY')}`;
};

export const getAge = (date: any) => {
  const dateDetails = DateTime.fromFormat(date, 'MM/dd/yyyy');
  const currentYear = new Date().getFullYear()
  const startYear = pathOr('0', ['c', 'year'], dateDetails);
  const age = Number(currentYear) - Number(startYear)
  return age;
}

/**
 * get the oldest date from given list of date string
 * @param { [string] } array of date time stamp string 
 * returns date object of sorted date
*/
export const getOldestDate = (dates: string[]) => {
  const minDate = new Date(
    Math.min(
      ...dates.map(element => {
        return new Date(element) as any;
      }),
    ),
  )
  return minDate
}

/**
 * get the latest date from given list of date string
 * @param { [string] } array of date time stamp string 
 *  returns date object of sorted date
*/
export const getLatestDate = (dates: string[]) => {
  const minDate = new Date(
    Math.max(
      ...dates.map(element => {
        return new Date(element) as any;
      }),
    ),
  )
  return minDate
}

export const getIncrementDateByDays = (date: any, numberOfdays: string) => {
  const days = returnIsNotEmptyOrTrue(numberOfdays, Number(numberOfdays), 0)
  if (isNotEmpty(date) && isNotEmpty(days) && days > 0) {
    return addDaysFromGivenDate(date, days, "MM/DD/YYYY");
  }
  return date;
}


/**
 * Adds a given number of days to the input date, then sets the result's day to a specific cutoff.
 *
 * @param inputDate - ISO 8601 or MM/DD/YYYY formatted date string.
 * @param daysToAdd - Number of days to add to the input date.
 * @param cutoffDay - Desired day of the resulting month (default: 25).
 * @returns Resulting date in MM/DD/YYYY format.
 */
export const calculateDueDateWithOffset = (
  inputDate: string,
  daysToAdd: string,
  cutoffDay: string = '25'
): string => {
  let date = DateTime.fromISO(inputDate);
  if (!date.isValid) {
    date = DateTime.fromFormat(inputDate, 'MM/dd/yyyy');
  }

  if (!date.isValid) {
    throw new Error(`Invalid date format: ${inputDate}`);
  }

  const addedDate = date.plus({ days: Number(daysToAdd) });
  const cutoff = Number(cutoffDay);

  // Determine correct cutoff date
  let dueDate;
  if (addedDate.day > cutoff) {
    // Move to cutoff of next month
    dueDate = addedDate.plus({ months: 1 }).set({ day: cutoff });
  } else {
    // Stay in same month
    dueDate = addedDate.set({ day: cutoff });
  }

  return dueDate.toFormat('MM/dd/yyyy');
};

export const getDaysFromDate = (date: any, comparingDate: any) => {
  const result = getDaysDifferenceBetweenTwoDate(new Date(comparingDate), DateTime.fromISO(date).toFormat('MM/dd/yyyy'))
  if (isNotEmpty(result) && isNotEmpty(date) && isNotEmpty(comparingDate)) {
    return toString(result);
  }
  return '0';
}


export const getMonthsFromDate = (date: any, comparingDate: any) => {
  const result = getMonthsDifferenceBetweenTwoDate(new Date(comparingDate), DateTime.fromISO(date).toFormat('MM/dd/yyyy'))
  if (isNotEmpty(result) && isNotEmpty(date) && isNotEmpty(comparingDate)) {
    return toString(result);
  }
  return '0';
}